export type RandomSource = () => number;

export class FeaturedImageCycle<TKey> {
    private featureStateByKey = new Map<TKey, boolean>();

    reset(imageKeys: readonly TKey[]): void {
        this.featureStateByKey = new Map(imageKeys.map(imageKey => [imageKey, false]));
    }

    reconcile(imageKeys: readonly TKey[]): void {
        this.featureStateByKey = new Map(imageKeys.map(imageKey => [
            imageKey,
            this.featureStateByKey.get(imageKey) === true
        ]));
    }

    markFeatured(imageKey: TKey): void {
        if (this.featureStateByKey.has(imageKey)) this.featureStateByKey.set(imageKey, true);
    }

    selectCandidate<T>(
        candidates: readonly T[],
        getKey: (candidate: T) => TKey,
        excludedKey: TKey | null,
        random: RandomSource = Math.random
    ): T | null {
        if (this.featureStateByKey.size > 0
            && [...this.featureStateByKey.values()].every(hasBeenFeatured => hasBeenFeatured)) {
            for (const imageKey of this.featureStateByKey.keys()) this.featureStateByKey.set(imageKey, false);
        }

        const unfeaturedCandidates = candidates.filter(candidate => this.featureStateByKey.get(getKey(candidate)) === false);
        return selectRandomCandidateByKey(
            unfeaturedCandidates.length > 0 ? unfeaturedCandidates : candidates,
            getKey,
            excludedKey,
            random);
    }
}

export function selectRandomIndex(candidateCount: number, random: RandomSource = Math.random): number | null {
    if (!Number.isInteger(candidateCount) || candidateCount < 0) {
        throw new RangeError('Candidate count must be a nonnegative integer.');
    }
    if (candidateCount === 0) return null;

    const randomValue = random();
    if (!Number.isFinite(randomValue) || randomValue < 0 || randomValue >= 1) {
        throw new RangeError('Random source must return a finite value greater than or equal to zero and less than one.');
    }

    return Math.floor(randomValue * candidateCount);
}

export function selectRandomCandidateByKey<T, TKey>(
    candidates: readonly T[],
    getKey: (candidate: T) => TKey,
    excludedKey: TKey | null,
    random: RandomSource = Math.random
): T | null {
    const candidatesByKey = new Map<TKey, T[]>();
    for (const candidate of candidates) {
        const key = getKey(candidate);
        const keyedCandidates = candidatesByKey.get(key);
        if (keyedCandidates == null) candidatesByKey.set(key, [candidate]);
        else keyedCandidates.push(candidate);
    }

    if (candidatesByKey.size === 0) return null;

    const nonExcludedGroups = [...candidatesByKey.entries()]
        .filter(([key]) => key !== excludedKey)
        .map(([, keyedCandidates]) => keyedCandidates);
    const candidateGroups = nonExcludedGroups.length > 0
        ? nonExcludedGroups
        : [...candidatesByKey.values()];
    const groupIndex = selectRandomIndex(candidateGroups.length, random);
    if (groupIndex == null) return null;

    const selectedGroup = candidateGroups[groupIndex];
    const candidateIndex = selectRandomIndex(selectedGroup.length, random);
    return candidateIndex == null ? null : selectedGroup[candidateIndex];
}
